import type {
  MultiselectCustomerPartialEntity,
  MultiselectParseResponse,
} from './configurationMultiselectTypes';

interface BuildMultiselectValidationSectionsInput {
  parsedData: MultiselectParseResponse;
  selectedIds: number[];
  data: MultiselectCustomerPartialEntity[];
  dataCheck?: MultiselectCustomerPartialEntity[];
  compareAsInt?: boolean;
}

interface BuildMultiselectSelectedSectionDataInput {
  selectedIds: number[];
  data: MultiselectCustomerPartialEntity[];
  compareAsInt?: boolean;
}

export interface MultiselectValidationSectionData {
  data: string[];
  dataToDisplay?: string[];
}

export interface MultiselectValidationSections {
  kept: MultiselectValidationSectionData;
  added: MultiselectValidationSectionData;
  dataCheckOnly?: MultiselectValidationSectionData;
  notRecognized: MultiselectValidationSectionData;
}

export const buildAdditiveMultiselectSelection = (
  selectedIds: number[],
  pastedIds: number[],
): number[] => {
  const selectedIdsSet = new Set(selectedIds);
  const idsToAdd = pastedIds.filter(id => {
    if (selectedIdsSet.has(id)) {
      return false;
    }

    selectedIdsSet.add(id);
    return true;
  });

  return [...selectedIds, ...idsToAdd];
};

const getMultiselectEntityIdentifier = (
  entity: MultiselectCustomerPartialEntity,
  compareAsInt: boolean = false,
): string => {
  if (compareAsInt) {
    return entity.id.toString();
  }

  return entity.customerId;
};

export const getMultiselectValidationEntityIdentifier = (
  entity: MultiselectCustomerPartialEntity,
  parsedData: MultiselectParseResponse,
  compareAsInt: boolean = false,
): string => {
  if (parsedData.parsedBy === 'code' && entity.code) {
    return entity.code;
  }

  return getMultiselectEntityIdentifier(entity, compareAsInt);
};

const buildRecognizedSectionData = (
  ids: number[],
  entitiesById: Map<number, MultiselectCustomerPartialEntity>,
  getIdentifier: (entity: MultiselectCustomerPartialEntity) => string,
): MultiselectValidationSectionData => {
  const sectionData = ids.map(id => {
    const entity = entitiesById.get(id);

    if (!entity) {
      const fallbackValue = id.toString();
      return {
        data: fallbackValue,
        dataToDisplay: fallbackValue,
      };
    }

    const identifier = getIdentifier(entity);

    return {
      data: identifier,
      dataToDisplay: entity.title ? `${identifier} - ${entity.title}` : identifier,
    };
  });

  return {
    data: sectionData.map(item => item.data),
    dataToDisplay: sectionData.map(item => item.dataToDisplay),
  };
};

export const buildMultiselectSelectedSectionData = ({
  selectedIds,
  data,
  compareAsInt = false,
}: BuildMultiselectSelectedSectionDataInput): MultiselectValidationSectionData => {
  const entitiesById = new Map(data.map(entity => [entity.id, entity]));

  return buildRecognizedSectionData(selectedIds, entitiesById, entity =>
    getMultiselectEntityIdentifier(entity, compareAsInt),
  );
};

export const buildMultiselectValidationSections = ({
  parsedData,
  selectedIds,
  data,
  dataCheck,
  compareAsInt = false,
}: BuildMultiselectValidationSectionsInput): MultiselectValidationSections => {
  const selectedIdsSet = new Set(selectedIds);
  const entitiesById = new Map([...(dataCheck ?? []), ...data].map(entity => [entity.id, entity]));

  const addedIds = parsedData.ids.filter(id => !selectedIdsSet.has(id));

  return {
    kept: buildRecognizedSectionData(selectedIds, entitiesById, entity =>
      getMultiselectValidationEntityIdentifier(entity, parsedData, compareAsInt),
    ),
    added: buildRecognizedSectionData(addedIds, entitiesById, entity =>
      getMultiselectValidationEntityIdentifier(entity, parsedData, compareAsInt),
    ),
    dataCheckOnly: dataCheck
      ? buildRecognizedSectionData(
          parsedData.dataCheckOnlyIds,
          new Map(dataCheck.map(entity => [entity.id, entity])),
          entity => getMultiselectValidationEntityIdentifier(entity, parsedData, compareAsInt),
        )
      : undefined,
    notRecognized: {
      data: parsedData.notRecognized.map(item => item.toString()),
    },
  };
};
